import { useEffect, useRef, useState } from 'react';

import { Input } from '@betterment/samba-react';
import { useField } from 'formik';

type DeferralValueFieldProps = {
  type: 'amount' | 'percent';
  name: string;
  disabled: boolean;
  setFormValue?: (newValue: number, unit: 'amount' | 'percent') => void;
};

const toNumeric = (text: string) => {
  const cleaned = text.replace(/,/g, '').trim();
  if (cleaned === '' || cleaned === '.') return null;
  const numeric = Number(cleaned);
  return Number.isFinite(numeric) ? numeric : null;
};

const formatDeferralValue = (value: number, type: 'amount' | 'percent'): string => {
  if (type === 'amount') {
    const cents = Number(value) || 0;
    return (cents / 100).toFixed(2);
  }
  const percent = Number(value) || 0;
  return percent.toFixed(2);
};

export const DeferralValueField = ({ type, name, disabled, setFormValue }: DeferralValueFieldProps) => {
  const [field] = useField<number>(name);
  const [inputText, setInputText] = useState<string>(formatDeferralValue(field.value, type));
  const isFocusedRef = useRef(false);

  useEffect(() => {
    if (!isFocusedRef.current) {
      setInputText(formatDeferralValue(field.value, type));
    }
  }, [field.value, type]);

  const handleFocus = () => {
    isFocusedRef.current = true;
    const displayedNumber = toNumeric(inputText);
    if (displayedNumber === null || displayedNumber === 0) {
      setInputText('');
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setInputText(inputValue);

    const numericValue = toNumeric(inputValue);
    if (numericValue === null) return;

    if (type === 'amount') {
      const cents = Math.round(numericValue * 100); // dollars → cents
      setFormValue?.(cents, 'amount');
    } else {
      setFormValue?.(numericValue, 'percent');
    }
  };

  const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    isFocusedRef.current = false;

    field.onBlur(event);

    const numericValue = toNumeric(inputText);
    const safeNumber = numericValue ?? 0;

    setInputText(safeNumber.toFixed(2));

    if (type === 'amount') {
      const cents = Math.round(safeNumber * 100);
      setFormValue?.(cents, 'amount');
    } else {
      setFormValue?.(safeNumber, 'percent');
    }
  };

  return (
    <Input
      mask="number"
      leadingSymbol={type === 'amount' ? '$' : undefined}
      trailingSymbol={type === 'percent' ? '%' : undefined}
      appearance="default"
      css={{ width: 'revert', marginRight: 'space.100' }}
      name={field.name}
      value={inputText}
      onFocus={handleFocus}
      onChange={handleInputChange}
      onBlur={handleInputBlur}
      disabled={disabled}
    />
  );
};
